/**
 * Dropdown menu on top of `Popover` (role="menu").
 *
 * Keys inside the menu: ↑/↓ move and wrap, Home/End jump, a letter jumps to
 * the next item starting with it, Enter/Space activate (native button/link),
 * Escape closes and returns focus to the trigger, Tab closes. Items are
 * `MenuItem` (an action) or `MenuLink` (a route); both close the menu.
 *
 * Pair with `useMenuTrigger()` for the opening button.
 */
import {
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type PointerEventHandler,
  type ReactNode,
  type RefObject,
} from "react";
import { Link, useMatch, useResolvedPath } from "react-router-dom";
import { clsx } from "clsx";

import { Popover, type PopoverPlacement } from "./Popover";
import { MenuContext, useMenuClose } from "./menuContext";
import type { MenuFocus } from "./useMenuTrigger";

interface MenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  id?: string;
  "aria-label"?: string;
  placement?: PopoverPlacement;
  /** Which item gets focus on open: an edge, an item index, or `"none"`. */
  focus?: MenuFocus | number;
  className?: string;
  /** Extra key handling (e.g. digit shortcuts); runs before the menu's own. */
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>, items: HTMLElement[]) => void;
  /** Hover mode: `useMenuTrigger().panelProps`. */
  onPointerEnter?: PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: PointerEventHandler<HTMLDivElement>;
  children: ReactNode;
}

function menuItems(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])'),
  );
}

export function Menu({
  open,
  onClose,
  anchorRef,
  id,
  placement = "bottom-start",
  focus = "first",
  className,
  onKeyDown,
  onPointerEnter,
  onPointerLeave,
  children,
  ...aria
}: MenuProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const initialRef = useRef<HTMLElement | null>(null);

  // Focus the initial item in the commit that opens the menu, not a frame
  // later: a quick "s 2 Enter" must already land inside the menu.
  useLayoutEffect(() => {
    if (!open) return;
    const items = menuItems(listRef.current);
    initialRef.current =
      typeof focus === "number"
        ? (items[focus] ?? items[0] ?? null)
        : focus === "none"
          ? null
          : focus === "last"
            ? (items[items.length - 1] ?? null)
            : (items[0] ?? null);
    initialRef.current?.focus({ preventScroll: true });
    // Re-focus only when the menu (re)opens, not when `focus` changes while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = menuItems(listRef.current);
    onKeyDown?.(e, items);
    if (e.defaultPrevented || items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const move = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === "ArrowDown") move(index + 1);
    else if (e.key === "ArrowUp") move(index < 0 ? items.length - 1 : index - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(items.length - 1);
    else if (e.key.length === 1 && /\S/.test(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const letter = e.key.toLowerCase();
      for (let step = 1; step <= items.length; step++) {
        const candidate = items[(index + step) % items.length];
        if ((candidate.textContent ?? "").trim().toLowerCase().startsWith(letter)) {
          move(items.indexOf(candidate));
          return;
        }
      }
    }
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      placement={placement}
      role="menu"
      id={id}
      initialFocus={focus === "none" ? "none" : initialRef}
      className={clsx("menu", className)}
      onKeyDown={handleKeyDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      {...aria}
    >
      <MenuContext.Provider value={{ close: onClose }}>
        <div ref={listRef} className="menu-list">
          {children}
        </div>
      </MenuContext.Provider>
    </Popover>
  );
}

interface MenuItemProps {
  onSelect: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
  /** Keep the menu open after selecting (e.g. a toggle). */
  keepOpen?: boolean;
  className?: string;
  children: ReactNode;
}

export function MenuItem({ onSelect, disabled, tone = "default", keepOpen, className, children }: MenuItemProps) {
  const close = useMenuClose();
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      className={clsx("menu-item", tone === "danger" && "menu-item--danger", className)}
      onClick={() => {
        if (disabled) return;
        onSelect();
        if (!keepOpen) close();
      }}
    >
      {children}
    </button>
  );
}

interface MenuLinkProps {
  to: string;
  end?: boolean;
  /** Overrides the route match (a menu that marks only its most specific item). */
  active?: boolean;
  className?: string;
  children: ReactNode;
}

export function MenuLink({ to, end = false, active: activeProp, className, children }: MenuLinkProps) {
  const close = useMenuClose();
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end });
  const active = activeProp ?? !!match;
  return (
    <Link
      to={to}
      role="menuitem"
      tabIndex={-1}
      aria-current={active ? "page" : undefined}
      className={clsx("menu-item", active && "is-active", className)}
      onClick={close}
    >
      {children}
    </Link>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="menu-separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="menu-label">{children}</div>;
}
